import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton, Alert,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  getAnalyticsSummary, getAnalyticsEvents,
  getAnalyticsChannelMix, getAnalyticsGeo, getAnalyticsCampaignCountry,
} from '../services/api';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const RANGES = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
];

function fmtPct(v) { return `${(v * 100).toFixed(1)}%`; }

const SOURCE_COLORS = {
  search: 'info',
  social: 'secondary',
  email: 'success',
  paid: 'warning',
  referral: 'default',
  direct: 'default',
  internal: 'primary',
  unknown: 'default',
};

function SourceCell({ source }) {
  if (!source || !source.category) return <span style={{ color: '#aaa' }}>—</span>;
  const tooltip = [source.category, source.hostname, source.isInternal ? '(internal)' : null]
    .filter(Boolean).join(' · ');
  return (
    <span title={tooltip}>
      <Chip size="small" label={source.category}
        color={SOURCE_COLORS[source.category] || 'default'}
        variant="outlined"
        sx={{ height: 18, fontSize: 10, mr: 0.5 }} />
      {source.hostname && (
        <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{source.hostname}</span>
      )}
    </span>
  );
}

function FirstTouchCell({ ft }) {
  if (!ft) return <span style={{ color: '#aaa' }}>—</span>;
  const date = ft.at ? new Date(ft.at).toLocaleDateString() : null;
  const tooltip = [
    ft.at ? `at: ${new Date(ft.at).toLocaleString()}` : null,
    ft.landingPage ? `landing: ${ft.landingPage}` : null,
    ft.referrer ? `referrer: ${ft.referrer}` : null,
    ft.utm?.campaign ? `campaign: ${ft.utm.campaign}` : null,
    ft.utm?.source ? `utm.source: ${ft.utm.source}` : null,
    ft.utm?.medium ? `utm.medium: ${ft.utm.medium}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span title={tooltip} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Chip size="small" label={ft.category || 'unknown'}
        color={SOURCE_COLORS[ft.category] || 'default'}
        variant="outlined"
        sx={{ height: 18, fontSize: 10 }} />
      {ft.hostname && <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{ft.hostname}</span>}
      {date && <span style={{ color: '#888', fontSize: 10 }}>· {date}</span>}
    </span>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
      </Stack>
    </Paper>
  );
}

function BreakdownTable({ title, rows, keyHeader }) {
  if (!rows?.length) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">No data.</Typography>
      </Paper>
    );
  }
  const max = Math.max(...rows.map(r => r.pageViews || 0), 1);
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{keyHeader}</TableCell>
            <TableCell align="right">Views</TableCell>
            <TableCell align="right">CTA</TableCell>
            <TableCell align="right">Demo</TableCell>
            <TableCell align="right">Conv.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.slice(0, 10).map(r => (
            <TableRow key={r.key} hover>
              <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} title={r.key}>{r.key}</Typography>
                <Box sx={{
                  mt: 0.5, height: 4, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden',
                }}>
                  <Box sx={{ width: `${(r.pageViews / max) * 100}%`, height: '100%', bgcolor: 'primary.main' }} />
                </Box>
              </TableCell>
              <TableCell align="right">{r.pageViews}</TableCell>
              <TableCell align="right">{r.ctaClicks}</TableCell>
              <TableCell align="right">{r.demoBookings}</TableCell>
              <TableCell align="right">
                <Chip size="small" label={fmtPct(r.conversionRate)} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function DailyChart({ rows }) {
  if (!rows?.length) return <Typography variant="body2" color="text.secondary">No daily data.</Typography>;
  const max = Math.max(...rows.map(r => r.pageViews || 0), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 160, px: 1 }}>
      {rows.map(r => {
        const h = (r.pageViews / max) * 100;
        return (
          <Box key={r.key} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
            <Box
              title={`${r.key}: ${r.pageViews} views, ${r.ctaClicks} CTA, ${r.demoBookings} demo`}
              sx={{
                width: '100%',
                height: `${Math.max(h, 2)}%`,
                bgcolor: r.demoBookings > 0 ? 'success.main' : 'primary.main',
                borderRadius: '3px 3px 0 0',
                transition: 'background-color 0.15s',
                '&:hover': { opacity: 0.8 },
              }}
            />
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled', mt: 0.5, transform: 'rotate(-45deg)', transformOrigin: 'top left', whiteSpace: 'nowrap' }}>
              {r.key.slice(5)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function AnalyticsPage() {
  const [range, setRange] = useState('30d');
  const [data, setData] = useState({ campaign: null, page: null, cta: null, day: null });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const days = (RANGES.find(r => r.key === range) || RANGES[1]).days;
      const to = new Date();
      const from = new Date(to.getTime() - days * 24 * 3600 * 1000);
      const params = { from: from.toISOString(), to: to.toISOString() };

      const [campaign, page, cta, day] = await Promise.all([
        getAnalyticsSummary({ ...params, groupBy: 'campaign' }),
        getAnalyticsSummary({ ...params, groupBy: 'page' }),
        getAnalyticsSummary({ ...params, groupBy: 'cta' }),
        getAnalyticsSummary({ ...params, groupBy: 'day' }),
      ]);
      setData({ campaign: campaign.data, page: page.data, cta: cta.data, day: day.data });
    } catch (e) {
      setErr(e?.response?.status === 403
        ? 'Admin role required to view marketing analytics.'
        : 'Failed to load analytics.');
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const totals = data.campaign?.totals;
  const dailyRows = useMemo(() => data.day?.rows || [], [data.day]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, overflow: 'auto', height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CampaignIcon color="primary" />
          <Typography variant="h4" sx={{ color: '#1a1a2e' }}>Marketing Analytics</Typography>
        </Stack>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)}>
          {RANGES.map(r => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {loading && !totals ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard icon={<VisibilityIcon />} label="Page views" value={totals?.pageViews ?? 0}
                sub={`${totals?.sessions ?? 0} sessions`} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard icon={<TouchAppIcon />} label="CTA clicks" value={totals?.ctaClicks ?? 0} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard icon={<EventAvailableIcon />} label="Demo bookings" value={totals?.demoBookings ?? 0} />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatCard icon={<TrendingUpIcon />} label="Conversion rate"
                value={fmtPct(totals?.conversionRate ?? 0)}
                sub="demos / page views" />
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Daily traffic</Typography>
            <DailyChart rows={dailyRows} />
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <ChannelMixWidget range={range} />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <GeoDistributionWidget range={range} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <CampaignCountryWidget range={range} />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <BreakdownTable title="By campaign" rows={data.campaign?.rows} keyHeader="utm_campaign" />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <BreakdownTable title="By page" rows={data.page?.rows} keyHeader="page" />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <BreakdownTable title="By CTA" rows={data.cta?.rows} keyHeader="cta label" />
            </Grid>
          </Grid>

          <RawEventsFeed range={range} />
        </>
      )}
    </Box>
  );
}

function RawEventsFeed({ range }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ event: '', sessionId: '', lid: '', cid: '' });
  const [draft, setDraft] = useState(filters);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const days = (RANGES.find(r => r.key === range) || RANGES[1]).days;
      const to = new Date();
      const from = new Date(to.getTime() - days * 24 * 3600 * 1000);
      const params = {
        from: from.toISOString(), to: to.toISOString(),
        page, pageSize,
        ...(filters.event ? { event: filters.event } : {}),
        ...(filters.sessionId ? { sessionId: filters.sessionId } : {}),
        ...(filters.lid ? { lid: filters.lid } : {}),
        ...(filters.cid ? { cid: filters.cid } : {}),
      };
      const res = await getAnalyticsEvents(params);
      setEvents(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* surface via summary alert at parent */ }
    finally { setLoading(false); }
  }, [range, page, pageSize, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [range, filters]);

  const apply = () => setFilters(draft);
  const reset = () => { setDraft({ event: '', sessionId: '', lid: '', cid: '' }); setFilters({ event: '', sessionId: '', lid: '', cid: '' }); };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Raw events <Chip size="small" label={total} sx={{ ml: 1 }} /></Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <TextField size="small" placeholder="event" value={draft.event}
            onChange={(e) => setDraft({ ...draft, event: e.target.value })} sx={{ width: 130 }} />
          <TextField size="small" placeholder="sessionId" value={draft.sessionId}
            onChange={(e) => setDraft({ ...draft, sessionId: e.target.value })} sx={{ width: 180 }} />
          <TextField size="small" placeholder="lid" value={draft.lid}
            onChange={(e) => setDraft({ ...draft, lid: e.target.value })} sx={{ width: 130 }} />
          <TextField size="small" placeholder="cid" value={draft.cid}
            onChange={(e) => setDraft({ ...draft, cid: e.target.value })} sx={{ width: 160 }} />
          <Button size="small" variant="contained" onClick={apply}>Apply</Button>
          <Button size="small" onClick={reset}>Reset</Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : !events.length ? (
        <Typography variant="body2" color="text.secondary">No events match.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Page</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Referrer</TableCell>
                <TableCell>First touch</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>lid / cid</TableCell>
                <TableCell>Campaign</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map(e => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {new Date(e.receivedAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={e.event}
                      color={e.event === 'cta_click' ? 'primary' : 'default'}
                      sx={{ height: 20, fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.page}>
                    {e.page || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <SourceCell source={e.source} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}
                    title={e.referrer || ''}>
                    {e.referrer
                      ? <a href={e.referrer} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{e.referrer}</a>
                      : <span style={{ color: '#aaa' }}>—</span>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    <FirstTouchCell ft={e.firstTouch} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }} title={e.sessionId}>
                    {(e.sessionId || '').slice(0, 8)}…
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    {e.lid || e.cid || <span style={{ color: '#aaa' }}>anon</span>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11 }}>{e.utmCampaign || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Page {page} / {totalPages}</Typography>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

function rangeParams(range) {
  const days = (RANGES.find(r => r.key === range) || RANGES[1]).days;
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 3600 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function ChannelMixWidget({ range }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    getAnalyticsChannelMix(rangeParams(range))
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [range]);
  const max = Math.max(...rows.map(r => r.visits || 0), 1);
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Channel mix</Typography>
      {loading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        : !rows.length ? <Typography variant="body2" color="text.secondary">No data.</Typography>
        : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell align="right">Visits</TableCell>
                <TableCell align="right">Sessions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.category} hover>
                  <TableCell>
                    <Chip size="small" label={r.category}
                      color={SOURCE_COLORS[r.category] || 'default'}
                      variant="outlined" sx={{ height: 20, fontSize: 11, mr: 1 }} />
                    <Box sx={{ mt: 0.5, height: 4, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
                      <Box sx={{ width: `${(r.visits / max) * 100}%`, height: '100%', bgcolor: 'primary.main' }} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">{r.visits}</TableCell>
                  <TableCell align="right">{r.sessions}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
    </Paper>
  );
}

function GeoDistributionWidget({ range }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    getAnalyticsGeo(rangeParams(range))
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [range]);
  const max = Math.max(...rows.map(r => r.sessions || 0), 1);
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Geographic distribution</Typography>
      {loading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        : !rows.length ? <Typography variant="body2" color="text.secondary">No country data yet.</Typography>
        : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Country</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">Visits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, 15).map(r => (
                <TableRow key={r.countryCode} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.countryCode}</Typography>
                    <Box sx={{ mt: 0.5, height: 4, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
                      <Box sx={{ width: `${(r.sessions / max) * 100}%`, height: '100%', bgcolor: 'success.main' }} />
                    </Box>
                  </TableCell>
                  <TableCell align="right">{r.sessions}</TableCell>
                  <TableCell align="right">{r.visits}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
    </Paper>
  );
}

function CampaignCountryWidget({ range }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    getAnalyticsCampaignCountry(rangeParams(range))
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [range]);

  const matrix = useMemo(() => {
    const campaigns = Array.from(new Set(rows.map(r => r.campaign)));
    const countries = Array.from(new Set(rows.map(r => r.countryCode)));
    const cell = {};
    rows.forEach(r => { cell[`${r.campaign}::${r.countryCode}`] = r.events; });
    const max = Math.max(...rows.map(r => r.events || 0), 1);
    return { campaigns, countries, cell, max };
  }, [rows]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Campaign × Country</Typography>
      {loading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        : !rows.length ? <Typography variant="body2" color="text.secondary">No campaign + country events.</Typography>
        : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  {matrix.countries.map(c => (
                    <TableCell key={c} align="center" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrix.campaigns.map(camp => (
                  <TableRow key={camp} hover>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }} title={camp}>{camp}</TableCell>
                    {matrix.countries.map(c => {
                      const v = matrix.cell[`${camp}::${c}`] || 0;
                      const intensity = v === 0 ? 0 : Math.min(1, v / matrix.max);
                      return (
                        <TableCell key={c} align="center" sx={{
                          fontSize: 11,
                          bgcolor: v === 0 ? 'transparent' : `rgba(25,118,210,${0.08 + intensity * 0.6})`,
                          color: intensity > 0.5 ? '#fff' : 'inherit',
                        }}>{v || ''}</TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
    </Paper>
  );
}
